import { Page } from 'next'
import { Hero } from '~/pages/homepage/components/hero'
import { FeatureList } from '~/pages/homepage/components/feature-list'
import { FeatureSection } from '../../../components/feature-section'
import { HomepageLayout } from '~/pages/homepage/layouts/homepage-layout'

import { ProductImage } from '~/pages/homepage/components/product-image'
import { RetinaImage } from '~/pages/homepage/components/retina-image'

import segmentationImage from './assets/segmentation.png'
import instantSearchImage from './assets/instant-search.jpeg'
import transactionsImage from './assets/transactions.png'
import analyticsImage from './assets/analytics.png'
import heroImage from './assets/hero.png'

export const NonprofitCrm: Page = () => {
  return (
    <>
      <div className="bg-[#FFEAD7] pb-40">
        <Hero
          title={'Nonprofit \nCRM'}
          description="All your data in one place. Track and manage donors, transactions, institutions and relationships between them with our full-fledged nonprofit CRM."
        >
          <div className="inline-flex lg:translate-x-12 lg:min-w-[748px]">
            <RetinaImage src={heroImage} priority alt="Kindest dashboard" />
          </div>
        </Hero>
        <FeatureList
          id="features"
          features={[
            {
              title: 'Donation Management',
              description:
                'Create and save custom reports, manage tax receipts or reply to new donations straight from your dashboard.',
            },
            {
              title: 'Unlimited Contacts',
              description:
                'With our unlimited CRM, you don’t ever have to worry about the size and growth of your audience.',
            },
            {
              title: 'Offline Donations',
              description:
                'A complete system for manual donation management with pledges, allocations, soft crediting and households.',
            },
            {
              title: 'Custom Segmentation',
              description:
                'Filter your audience based on tags or donation history, or use pre-defined conditions like LYBUNTY or SYBUNTY.',
            },
            {
              title: 'Integrations',
              description:
                'We work with all of your favorite tools. Expand the reach of your nonprofit CRM by connecting your favorite software.',
            },
            {
              title: 'Integrated Analytics',
              description:
                'Track donation data, quickly identify trends and take action to improve your ongoing campaigns.',
            },
          ]}
        />
      </div>

      <div className="pb-20 border-b md:pt-20 md:pb-36">
        <FeatureSection
          id="donor-segmentation"
          label="Custom Segmentation"
          title="Have Control Over Your Audience"
          description="Bring a better way of organization to your donor management with custom segmentation. By combining smart filters and pre-defined templates, you will always be just one click from the data you need."
        >
          <ProductImage src={segmentationImage} alt="Segmentation" />
        </FeatureSection>
        <FeatureList
          features={[
            {
              title: 'Pre-Defined Templates',
              description:
                'Seamless filtering thanks to pre-defined templates like SYBUNTY, LYBUNTY or recurring donors.',
            },
            {
              title: 'Custom Segments',
              description:
                'Create, save and manage your own segments. The list of all segments gives you a perfect overview of your audience distribution.',
            },
            {
              title: 'Outbound Marketing',
              description:
                'Send text messages or Facebook campaigns to segments of your choice.',
            },
          ]}
        />
      </div>

      <FeatureSection
        id="powerful-search"
        label="Powerful Search Engine"
        title="Find Donors and Transactions in an Instant"
        description="Finding the right donor or transaction has never been easier. Just write the email, name or amount and our powerful search engine will show you the result instantly. No need to scroll through hundreds of records anymore."
      >
        <RetinaImage src={instantSearchImage} alt="Search bar" />
      </FeatureSection>
      <FeatureSection
        id="reporting"
        label="Custom Reporting"
        title="Complex Reports Done Easily"
        description="Create donation reports with custom filters and multiple conditions. Take advantage of our templates to generate one-click reports or make your own presets."
        reverse
      >
        <ProductImage src={transactionsImage} alt="Reporting" />
      </FeatureSection>

      <div className="bg-[#F3F6F9] md:pt-20 pb-20 md:pb-36">
        <FeatureSection
          label="Integrated Analytics"
          title="Track Data and Make Informed Decisions"
          description="Visualized donor data helps you make the right decisions. Stay informed with our advanced analytics tools and steer your fundraising based on real activities rather than assumptions."
        >
          <ProductImage src={analyticsImage} alt="Analytics" />
        </FeatureSection>
        <FeatureList
          features={[
            {
              title: 'Source Breakdown',
              description:
                'Learn the exact source of your donation whether it came from an email, social media or an SMS campaign.',
            },
            {
              title: 'Week Days Performance',
              description:
                'Track performance of each week day based on money raised and plan your campaigns accordingly.',
            },
            {
              title: 'SMS Funnel',
              description:
                'Optimize your SMS campaigns with the data percentage of sent, received, opened and clicked text messages.',
            },
          ]}
        />
      </div>

      <FeatureList
        className="py-20 md:py-36"
        id="donor-management"
        title="Donor Management That Makes Your Life Easier"
        features={[
          {
            title: 'Soft Crediting',
            description:
              'Add additional contacts to any donation to give credit and track your refferal network.',
          },
          {
            title: 'Pledges',
            description:
              'Make a record of a future donation that was pledged by an individual or institution. ',
          },
          {
            title: 'Exports and Imports',
            description:
              'Work with third party systems and export and import donor records and transactions.',
          },
          {
            title: 'Custom Notes',
            description:
              'Keep important information about your donors, institutions or transactions right within their accounts.',
          },
          {
            title: 'Institutions',
            description:
              'Create and manage households, corporations, churches, DAFs or any other type of organization.',
          },
          {
            title: 'Roles and Permissions',
            description:
              'Invite unlimited numbers of admins, creators or accountants. Each role has a different level of access.',
          },
          {
            title: 'Reply Tasks',
            description:
              'Engage your new donors and reply to every new donation with pre-defined and custom answers.',
          },
          {
            title: 'Donor Tagging',
            description:
              'Asign tags to donors to create new segmenets and build your own organization structure.',
          },
          {
            title: 'Relationships',
            description:
              'Build and track relationships between families, employees or church members.',
          },
        ]}
      />
    </>
  )
}

NonprofitCrm.getLayout = HomepageLayout
